import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { analyticsAPI } from '../utils/api';

const createSessionId = () => `session_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

const VisitorTracker = () => {
  const { pathname } = useLocation();
  const sessionId = useRef(null);
  const startTime = useRef(Date.now());
  const pageViewCount = useRef(0);
  const visitedPages = useRef(new Set());
  const initialized = useRef(false);

  const sessionPayload = () => {
    const duration = Math.floor((Date.now() - startTime.current) / 1000);
    return {
      visit_duration: duration,
      page_views: pageViewCount.current,
      is_bounce: pageViewCount.current === 1 && duration < 30,
      last_activity: new Date().toISOString()
    };
  };

  useEffect(() => {
    if (initialized.current) return undefined;
    initialized.current = true;

    let storedSession = sessionStorage.getItem('visitor_session_id');
    if (!storedSession) {
      storedSession = createSessionId();
      sessionStorage.setItem('visitor_session_id', storedSession);
    }
    sessionId.current = storedSession;

    const urlParams = new URLSearchParams(window.location.search);
    analyticsAPI.trackVisitor({
      session_id: storedSession,
      ip_address: '',
      user_agent: navigator.userAgent,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language || 'ru',
      referrer: document.referrer || '',
      landing_page: window.location.pathname,
      utm_source: urlParams.get('utm_source') || '',
      utm_medium: urlParams.get('utm_medium') || '',
      utm_campaign: urlParams.get('utm_campaign') || ''
    }).catch(() => {});

    visitedPages.current.add(window.location.pathname);
    pageViewCount.current = 1;

    const updateSession = () => {
      if (sessionId.current) analyticsAPI.updateVisitor(sessionId.current, sessionPayload()).catch(() => {});
    };
    const intervalId = window.setInterval(() => {
      if (!document.hidden) updateSession();
    }, 30000);
    const onVisibilityChange = () => { if (document.hidden) updateSession(); };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (!sessionId.current || visitedPages.current.has(pathname)) return;
    visitedPages.current.add(pathname);
    pageViewCount.current += 1;
    analyticsAPI.updateVisitor(sessionId.current, {
      ...sessionPayload(),
      pages_visited: [pathname]
    }).catch(() => {});
  }, [pathname]);

  return null;
};

export default VisitorTracker;
